import { cn } from '@workspace/ui/lib/utils'
import { copy } from '../../copy/investor-copy'
import { formatShortDate } from './dates'

const DAY_MS = 24 * 60 * 60 * 1000

/** "Today", "Yesterday", else "10 Oct". */
function latestLabel(latestAt: Date | string): string {
  const date = new Date(latestAt)
  const startOfToday = new Date()
  startOfToday.setHours(0, 0, 0, 0)
  const age = startOfToday.getTime() - date.getTime()
  if (age <= 0) return 'Today'
  if (age <= DAY_MS) return 'Yesterday'
  return formatShortDate(date)
}

/** One bar per week, the newest on the right: 3px for none, 7px, 11px and 15px for one, two and three or more. */
const barHeight = (count: number) => (count === 0 ? 3 : count === 1 ? 7 : count === 2 ? 11 : 15)

/**
 * What the founder shared since the follow, week by week for twelve weeks, and when last; a dot while
 * an alert is unread. Until the first update there are no bars, only the day the follow started.
 */
export function FounderUpdates({
  weekly,
  latestAt,
  followedAt,
  unread,
}: {
  weekly: number[]
  latestAt: Date | string | null
  followedAt: Date | string
  unread: boolean
}) {
  const dot = unread ? (
    <span
      role="img"
      aria-label="Unread update"
      className="size-1.5 shrink-0 rounded-full bg-primary"
    />
  ) : (
    <span aria-hidden className="size-1.5 shrink-0" />
  )
  if (!latestAt)
    return (
      <div className="flex items-center gap-2">
        {dot}
        <span className="whitespace-nowrap text-xs text-muted-foreground">
          {copy('row.updates.none', { date: formatShortDate(followedAt) })}
        </span>
      </div>
    )
  return (
    <div className="flex items-center gap-2">
      {dot}
      <span
        role="img"
        aria-label="Founder updates per week, last 12 weeks"
        className="inline-flex h-[15px] items-end gap-0.5"
      >
        {weekly.map((count, i) => (
          <i
            // The weeks never reorder, so the position is the identity.
            // biome-ignore lint/suspicious/noArrayIndexKey: fixed twelve-week window
            key={i}
            className={cn(
              'block w-[3px] rounded-[1px] bg-muted-foreground',
              count > 0 ? 'opacity-85' : 'opacity-30'
            )}
            style={{ height: barHeight(count) }}
          />
        ))}
      </span>
      <span className="whitespace-nowrap text-xs tabular-nums text-foreground/80">
        {latestLabel(latestAt)}
      </span>
    </div>
  )
}
